import { useState, useContext, useEffect } from "react";
import {
    KmsTypeCategoryMembership,
    KmsTypePublishEntryCategories,
    KmsTypePublishUserCategories,
} from "@kaltura/mediaspace-shared-types";
import { Tabs, Tab } from "@kaltura/ds-react-components";
import { TabPanel, tabProps } from "@kaltura/mediaspace-shared-ui";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config/ConfigContext";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { ChannelsTab } from "./channels-tab/ChannelsTab";
import { GalleriesTab } from "./galleries-tab/GalleriesTab";
import { ExceptionsTab } from "./exceptions-tab/ExceptionsTab";
import { ExternalTab } from "./external-tab/ExternalTab";

export interface PublishTabsProps {
    entryCategories: KmsTypePublishEntryCategories;
    userCategories: KmsTypePublishUserCategories;
    entryIds: string[];
    crossApplicationUnpublishing: boolean;
    onCategoryChange: (categoryId: number, checked: boolean) => void;
    onUnpublishExternal: () => void;
    isBulk?: boolean;
    disabledCategoryMembershipIds?: KmsTypeCategoryMembership[];
}

enum TabsValue {
    channels = "channels",
    galleries = "galleries",
    exceptions = "exceptions",
    external = "external",
}

/**
 * Publish modal tabs
 */
export function PublishTabs(props: PublishTabsProps) {
    const {
        entryCategories,
        userCategories,
        entryIds,
        crossApplicationUnpublishing,
        onCategoryChange,
        onUnpublishExternal,
        isBulk,
        disabledCategoryMembershipIds,
    } = props;

    const { channels = {}, galleries = {}, externalCategories = [] } = entryCategories;
    const { myChannels, moreChannels, galleries: userGalleries } = userCategories;

    const userChannels = myChannels || moreChannels;
    const [tabValue, setTabValue] = useState(userChannels ? TabsValue.channels : TabsValue.galleries);

    const configContext = useContext(ConfigContext);
    const isKaf = configContext.application?.isKAF;

    const hasPendingRejected =
        !!channels?.pending?.length
        || !!galleries?.pending?.length
        || !!channels?.rejected?.length
        || !!galleries?.rejected?.length;

    const hasExternalCategories = externalCategories.length > 0;

    const sendButtonAnalytics = useButtonAnalytics();

    const entryId = entryIds.length === 1 ? entryIds[0] : undefined;

    // send analytics for default tab (channels)
    useEffect(() => {
        sendButtonAnalytics(
            `Publish - Set publishing settings modal box - ${TabsValue.channels} tab ${isBulk ? "(bulk)" : ""}`,
            ButtonClickAnalyticsType.BROWSE,
            entryId
        );
    }, []);

    // send analytics for tab change
    const handleTabChange = (event: React.SyntheticEvent, newValue: TabsValue) => {
        setTabValue(newValue);

        sendButtonAnalytics(
            `Publish - Set publishing settings modal box - ${newValue} tab ${isBulk ? "(bulk)" : ""}`,
            ButtonClickAnalyticsType.BROWSE,
            entryId
        );
    };

    const handlePendingClick = () => {
        setTabValue(TabsValue.exceptions);
    };

    return (
        <>
            <Tabs
                className="kms-ds-publish-locations-tabs"
                value={tabValue}
                onChange={handleTabChange}
                aria-label={translate("Publishing locations")}
            >
                {userChannels && (
                    <Tab key="tab-channels" label={translate("Channels")} {...tabProps(TabsValue.channels)} />
                )}
                {!isKaf && userGalleries && (
                    <Tab key="tab-galleries" label={translate("Galleries")} {...tabProps(TabsValue.galleries)} />
                )}
                {hasPendingRejected && (
                    <Tab key="tab-exceptions" label={translate("Exceptions")} {...tabProps(TabsValue.exceptions)} />
                )}
                {hasExternalCategories && (
                    <Tab key="tab-external" label={translate("External")} {...tabProps(TabsValue.external)} />
                )}
            </Tabs>
            {userChannels && (
                <TabPanel key="panel-channels" currentTab={tabValue} name={TabsValue.channels}>
                    <ChannelsTab
                        entryIds={entryIds}
                        channels={channels}
                        userCategories={userCategories}
                        onCategoryChange={onCategoryChange}
                        onPendingClick={handlePendingClick}
                        isBulk={isBulk}
                        disabledCategoryMembershipIds={disabledCategoryMembershipIds}
                    />
                </TabPanel>
            )}
            {!isKaf && userGalleries && (
                <TabPanel key="panel-galleries" currentTab={tabValue} name={TabsValue.galleries}>
                    <GalleriesTab
                        entryIds={entryIds}
                        galleries={galleries}
                        userCategories={userCategories}
                        onCategoryChange={onCategoryChange}
                        onPendingClick={handlePendingClick}
                        isBulk={isBulk}
                    />
                </TabPanel>
            )}
            {hasPendingRejected && (
                <TabPanel key="panel-exceptions" currentTab={tabValue} name={TabsValue.exceptions}>
                    <ExceptionsTab
                        entryIds={entryIds}
                        channels={channels}
                        galleries={galleries}
                        onCategoryChange={onCategoryChange}
                        isBulk={isBulk}
                    />
                </TabPanel>
            )}
            {hasExternalCategories && (
                <TabPanel key="panel-external" currentTab={tabValue} name={TabsValue.external}>
                    <ExternalTab
                        entryIds={entryIds}
                        externalCategories={externalCategories}
                        crossApplicationUnpublishing={crossApplicationUnpublishing}
                        onUnpublishExternal={onUnpublishExternal}
                        isBulk={isBulk}
                    />
                </TabPanel>
            )}
        </>
    );
}
